import { useContext, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DialogContentText } from '@mui/material';

import { AppContext } from '../../../App';
import useRerenderDocumentPages from '../../hooks/useRerenderDocumentPages';
import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';

interface RerenderDocumentPagesProps {
  dpid: string;
  documentId: string;
}

const RerenderDocumentPages = ({ dpid, documentId }: RerenderDocumentPagesProps) => {
  const { setToast } = useContext(AppContext);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const rerenderPages = useRerenderDocumentPages(dpid, documentId);

  const closeConfirmation = () => {
    if (!rerenderPages.isPending) {
      setConfirmationOpen(false);
    }
  };

  const confirmRerender = () => {
    rerenderPages.mutate(undefined, {
      onSuccess: () => {
        setConfirmationOpen(false);
        setToast({
          open: true,
          variant: 'success',
          message:
            'Page re-rendering queued. Reload this page in a few moments to see the updated images.',
        });
      },
      onError: (error) => {
        setToast({
          open: true,
          variant: 'error',
          message: `Could not start page re-rendering: ${error.message}`,
        });
      },
    });
  };

  return (
    <>
      <IconButtonWhiteBg
        onClick={() => setConfirmationOpen(true)}
        disabled={rerenderPages.isPending}
        id="btn-rerender-document-pages"
        cypressId="btn-rerender-document-pages"
        tooltip="Re-render page images"
        sx={{ mr: 1 }}
      >
        <RefreshIcon sx={{ fontSize: '14px', color: 'black' }} />
      </IconButtonWhiteBg>

      <DialogGlynt
        open={confirmationOpen}
        closeModal={closeConfirmation}
        loading={rerenderPages.isPending}
        width="440px"
        cypressId="dialog-rerender-document-pages"
      >
        <DialogTitleGlynt
          title="Re-render Page Images"
          closeModal={closeConfirmation}
          startIcon="update"
          loading={rerenderPages.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>
            Only do this if the document preview is blurry, incomplete, or otherwise looks wrong.
            This regenerates every page image and may take a few moments. It does not change the
            document or its extracted data.
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={confirmRerender}
          closeModal={closeConfirmation}
          cancelCypressId="btn-rerender-pages-cancel"
          submitCypressId="btn-rerender-pages-confirm"
          clearCypressId=""
          submitButtonText="Re-render Pages"
          submitLoading={rerenderPages.isPending}
          submitDisabled={rerenderPages.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default RerenderDocumentPages;
